import React from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import VerifiedAdvocateBadge from "@user/components/VerifiedAdvocateBadge";
import { Row, fmtTime } from "@shared/consultation/roomUtils";

// Left-panel "Consultation Summary" card: parties, status/payment rows, query, and lawyer start/complete controls.
export function ConsultationSummaryCard({ consult, isLawyerSide, isLive, isTerminal, isCancelled, session, canStart, canComplete, busy, onStart, onComplete }) {
  return (
    <div className="box-border w-full max-w-full min-w-0 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="consult-summary-card">
      <div className="flex min-w-0 items-center gap-3">
        {(isLawyerSide ? null : consult.lawyer_photo) ? (
          <img src={consult.lawyer_photo} alt="" className="h-12 w-12 rounded-xl object-cover" />
        ) : (
          <div className="h-12 w-12 rounded-xl adv-navy text-[#D4AF37] grid place-items-center font-bold">
            {(isLawyerSide ? consult.user_name : consult.lawyer_name)?.split(" ").slice(-2).map((x) => x[0]).join("") || "C"}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="min-w-0 break-words font-bold text-[#0A2540]" data-testid="consult-other-name">
              {isLawyerSide ? consult.user_name : consult.lawyer_name}
            </p>
            {!isLawyerSide && (
              <VerifiedAdvocateBadge
                lawyer={{
                  id: consult.lawyer_id,
                  verified_advocate: consult.lawyer_verified_advocate,
                  bar_council_status: consult.lawyer_bar_council_status,
                }}
                size="sm"
              />
            )}
          </div>
          <p className="text-xs text-slate-500 capitalize">{consult.consultation_type} consultation</p>
        </div>
      </div>
      <div className="mt-4 min-w-0 space-y-2 text-sm">
        <Row k="Status" v={
          <span className={`capitalize font-bold ${isLive ? "text-emerald-600" : isCancelled ? "text-red-600" : "text-[#0A2540]"}`} data-testid="consult-status">
            {isLive && <span className="adv-pulse-dot inline-block mr-1" />}{consult.status === "lawyer_no_show"
              ? consult.lawyer_no_show_stage === "initiation" ? "Lawyer didn't initiate"
                : consult.lawyer_no_show_stage === "start" ? "Lawyer didn't start" : "Lawyer didn't join"
              : consult.status.replace(/_/g, " ")}
          </span>
        } />
        <Row k="Payment" v={<span className={`capitalize font-bold ${consult.payment_status === "paid" ? "text-emerald-600" : "text-amber-700"}`}>{consult.payment_status}</span>} />
        <Row k="Amount" v={<span className="font-bold">₹{consult.amount}</span>} />
        {consult.pricing_snapshot && (
          <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs space-y-1">
            {(consult.pricing_snapshot.market_fee_paise || 0) > 0 && (
              <div className="flex justify-between"><span className="text-slate-500 line-through">Market Fee (MRP)</span><span className="text-slate-400 line-through">₹{(consult.pricing_snapshot.market_fee_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            )}
            {(consult.pricing_snapshot.customer_price_before_discount_paise || 0) > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Customer price before discount</span><span className="font-medium text-[#0A2540]">₹{((consult.pricing_snapshot.customer_price_before_discount_paise ?? 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            )}
            {(consult.pricing_snapshot.platform_discount_paise || 0) > 0 && (
              <div className="flex justify-between"><span className="text-[#D4AF37]">Platform Discount ({(consult.pricing_snapshot.platform_discount_pct_bps / 100).toFixed(0)}%)</span><span className="font-medium text-[#D4AF37]">−₹{(consult.pricing_snapshot.platform_discount_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            )}
            {(consult.pricing_snapshot.discount_paise || 0) > 0 && (
              <div className="flex justify-between"><span className="text-emerald-700">{consult.pricing_snapshot.discount_applied === "first" ? "First Consultation Offer" : "Promotional Discount"}</span><span className="font-medium text-emerald-700">−₹{(consult.pricing_snapshot.discount_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">GST ({(consult.pricing_snapshot.gst_rate_bps || 1800) / 100}%)</span><span className="font-medium text-[#0A2540]">₹{(consult.pricing_snapshot.gst_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1"><span className="font-bold text-[#0A2540]">Total paid</span><span className="font-bold text-[#0A2540]">₹{(consult.pricing_snapshot.charged_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
          </div>
        )}
        {consult.slot_date && <Row k="Slot" v={`${consult.slot_date} · ${consult.slot_time}`} />}
        {consult.invoice_no && <Row k="Invoice" v={consult.invoice_no} />}
        {consult.started_at && isTerminal && <Row k="Started" v={fmtTime(consult.started_at)} />}
        {consult.completed_at && <Row k="Ended" v={fmtTime(consult.completed_at)} />}
        <Row k="Session" v={
          <span className={`text-[10px] font-bold uppercase tracking-wider ${session.text}`} data-testid="consult-connection" data-state={session.key}>
            <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 ${session.dot}`} />
            {session.label}
          </span>
        } />
      </div>
      <div className="mt-4 min-w-0 pt-4 border-t border-slate-100">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Query</p>
        <p className="mt-1 break-words text-sm text-slate-700 [overflow-wrap:anywhere]">{consult.query}</p>
      </div>

      {/* Lawyer controls */}
      {canStart && (
        <Button onClick={onStart} disabled={busy} data-testid="start-consult-btn"
          className="mt-4 h-11 w-full max-w-full rounded-xl font-bold adv-btn-gold">
          <Play size={16} className="mr-2" /> Start consultation
        </Button>
      )}

      {canComplete && (
        <Button onClick={onComplete} variant="outline" disabled={busy} data-testid="complete-btn"
          className="mt-2 h-11 w-full max-w-full rounded-xl font-bold">
          Mark complete
        </Button>
      )}
    </div>
  );
}
